import { RulesLogic } from 'json-logic-js';
import { max } from 'lodash/fp';
import { FC, PropsWithChildren, ReactNode, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import {
  LAYOUT_COLUMN_TOTAL,
  LAYOUT_ROW_HEIGHT,
  MODULE_ERROR_MESSAGE,
  MODULE_TYPE_TO_NAME,
} from 'dashboard/apps/DashboardLayoutEditor/constants';
import { performTopCompaction } from 'dashboard/apps/DashboardLayoutEditor/gridPositioningUtils';
import { generateRunAndReturnSnippet } from 'dashboard/components/GridLayoutRender/computed-properties/helpers';
import PersonaErrorBoundary from 'dashboard/components/PersonaErrorBoundary';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Module, ModulePosition } from 'dashboard/lib/module';
import { printMixins } from 'dashboard/lib/printMixins';
import Placeholder from 'dashboard/modules/Placeholder';
import { showMessage } from 'dashboard/redux/actions/messages';
import Card from 'design-system/components/Card';
import { captureException } from 'lib/reporting';

/**
 * NOTE:
 * Our module position configuration assumes a 12-column grid system with an 8px row height.
 * When computing the final module layout, we convert the height to pixels so we can achieve
 * pixel-perfect rendering.
 */

const GridContainer = styled('div')<{ rows: number }>`
  display: grid;
  grid-template-columns: repeat(${LAYOUT_COLUMN_TOTAL}, 1fr);
  grid-template-rows: ${({ rows }) => `repeat(${rows}, 1px)`};
`;

export function useModuleLayout<M extends Module>(
  currentModules: M[],
  runCodeWithContext: (code: string) => Promise<unknown>,
  runJsonLogicWithContext: (rules: RulesLogic) => Promise<boolean>,
) {
  const dispatch = useDispatch();
  const [moduleMetadata, setModuleMetadata] = useState(
    currentModules.reduce<ModuleMetadataMap>((acc, m) => {
      acc[m.token] = {
        // module visibility defaults to false if the "visible" computed property is defined
        visible: !m['module-layout'].visible,
        moduleHeight: m.position.height * LAYOUT_ROW_HEIGHT,
      };
      return acc;
    }, {}),
  );
  // take the original modules and reconcile them with computed module metadata
  const computedModules = currentModules.reduce<M[]>((acc, m) => {
    const { moduleHeight, visible } = moduleMetadata[m.token];

    if (!visible) return acc;

    acc.push({ ...m, position: { ...m.position, height: moduleHeight } });
    return acc;
  }, []);
  performTopCompaction(computedModules);

  const handleUpdateModuleHeight = (moduleToken: string, moduleHeight: number) => {
    setModuleMetadata((prevModuleMetadata) => {
      return {
        ...prevModuleMetadata,
        [moduleToken]: {
          ...prevModuleMetadata[moduleToken],
          moduleHeight,
        },
      };
    });
  };

  const handleUpdateModuleVisibility = (moduleToken: string, visible: boolean) => {
    setModuleMetadata((prevModuleMetadata) => {
      return {
        ...prevModuleMetadata,
        [moduleToken]: {
          ...prevModuleMetadata[moduleToken],
          visible,
        },
      };
    });
  };

  useEffect(() => {
    currentModules.forEach((m) => {
      if (!m['module-layout'].visible) return;

      if (m['module-layout'].visible['resolution-mechanism']['resolution-type'] === 'custom-code') {
        const returnType = m['module-layout'].visible['return-type'];

        if (!m['module-layout'].visible['resolution-mechanism']['serialized-code']) return;

        const deserializedCode = atob(
          m['module-layout'].visible['resolution-mechanism']['serialized-code'],
        );

        runCodeWithContext(generateRunAndReturnSnippet(deserializedCode))
          .then((returnValue) => {
            if (typeof returnValue !== 'boolean') {
              throw new TypeError(`dynamic layout custom code must return a ${returnType} value`);
            }

            handleUpdateModuleVisibility(m.token, returnValue);
          })
          .catch((error) => {
            console.error(m.type, error);
            dispatch(
              showMessage(
                `We are experiencing issues loading the ${m.type} module. Please contact an administrator if this error persists.`,
                MessageType.Failure,
              ),
            );
          });
      } else if (
        m['module-layout'].visible['resolution-mechanism']['resolution-type'] ===
        'conditional-logic'
      ) {
        const rules = m['module-layout'].visible['resolution-mechanism'].conditional?.rules;

        // The following line complains about the rules of type LogicBlockOrV2 not matching
        // the type RulesLogic it accepts but they do
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        runJsonLogicWithContext(rules)
          .then((returnValue) => {
            handleUpdateModuleVisibility(m.token, returnValue);
          })
          .catch((error) => {
            console.error(m.type, error);
            dispatch(
              showMessage(
                `We are experiencing issues loading the ${m.type} module. Please contact an administrator if this error persists.`,
                MessageType.Failure,
              ),
            );
          });
      }
    });
  }, [currentModules, dispatch, runCodeWithContext, runJsonLogicWithContext]);

  return { computedModules, handleUpdateModuleHeight };
}

interface ModuleMetadataMap {
  [key: string]: {
    visible: boolean;
    moduleHeight: number;
  };
}
interface ModuleLayoutProps<M extends Module> {
  modules: M[];
  renderModule: (module: M) => ReactNode;
  runCodeWithContext: (code: string) => Promise<unknown>;
  runJsonLogicWithContext: (rules: RulesLogic) => Promise<boolean>;
}

export function ModuleLayout<M extends Module>({
  modules: currentModules,
  renderModule,
  runCodeWithContext,
  runJsonLogicWithContext,
}: ModuleLayoutProps<M>) {
  const { computedModules, handleUpdateModuleHeight } = useModuleLayout(
    currentModules,
    runCodeWithContext,
    runJsonLogicWithContext,
  );

  // Get the lowest row we need (max of row + height of all modules in this tab)
  const maxRows =
    max(computedModules.map((module: M) => module.position.y + module.position.height)) ?? 0;

  const cards = computedModules.map((module) => {
    return (
      <PersonaErrorBoundary
        key={module.token}
        onCatch={(error) =>
          captureException(error, 'error', {
            error,
            module: module.token,
            component: 'ModuleLayout',
          })
        }
        fallback={() => (
          <ModuleContainer position={module.position}>
            <StyledModuleCard disableOverflow={false}>
              <Placeholder
                title={MODULE_TYPE_TO_NAME[module.type]}
                message={MODULE_ERROR_MESSAGE}
              />
            </StyledModuleCard>
          </ModuleContainer>
        )}
      >
        <GridModule module={module} onModuleHeightUpdate={handleUpdateModuleHeight}>
          {renderModule(module)}
        </GridModule>
      </PersonaErrorBoundary>
    );
  });

  return <GridContainer rows={maxRows}>{cards}</GridContainer>;
}

// TODO: [melissa] does NOT check for grid out of bounds
const CARD_PADDING = 8;
interface ModuleContainerProps {
  position: ModulePosition;
}
const ModuleContainer: FC<PropsWithChildren<ModuleContainerProps>> = ({ position, children }) => {
  const { paddingLeft, paddingRight, paddingBottom, paddingTop } = calculateCardPadding(position);

  return (
    <div
      style={{
        // Styles are inlined because module heights can be recalculated many times during an
        // accordion open/close animation and inlined styles are significantly more performant
        gridColumn: `${position.x + 1} / span ${position.width}`,
        gridRow: `${position.y + 1} / span ${position.height}`,
        paddingLeft: `${paddingLeft}px`,
        paddingRight: `${paddingRight}px`,
        paddingTop: `${paddingTop}px`,
        paddingBottom: `${paddingBottom}px`,
      }}
    >
      {children}
    </div>
  );
};

const calculateCardPadding = (position: ModulePosition) => {
  const paddingLeft = position.x <= 0 ? 0 : CARD_PADDING;
  const paddingRight = position.x + position.width >= LAYOUT_COLUMN_TOTAL - 1 ? 0 : CARD_PADDING;
  const paddingTop = position.y <= 0 ? 0 : CARD_PADDING;
  const paddingBottom = CARD_PADDING;

  return { paddingLeft, paddingRight, paddingBottom, paddingTop };
};

const StyledModuleCard = styled(Card)<{ disableOverflow: boolean }>`
  overflow-y: ${({ disableOverflow }) => (disableOverflow ? 'hidden' : 'auto')};
  overflow-x: auto;
  height: 100%;
  margin: 0;
`;

const ModulePrintWrapper = styled.div`
  height: 100%;
  border-radius: ${({ theme }) => theme.card.borderRadius};

  @media print {
    ${printMixins.border}
  }
`;

interface GridModuleProps<M extends Module> {
  module: M;
  onModuleHeightUpdate: (token: string, moduleHeight: number) => void;
}

// TODO: (melissa) include card styling here instead of in individual modules
function GridModule<M extends Module>({
  module,
  onModuleHeightUpdate,
  children,
}: PropsWithChildren<GridModuleProps<M>>) {
  const ref = useRef<HTMLDivElement>();
  const hasDynamicHeight = module['module-layout']['dynamic-height'];

  useEffect(() => {
    if (!hasDynamicHeight || !ref.current) return;

    const moduleChildren = Array.from(ref.current.children);

    const resizeObserver = new ResizeObserver(() => {
      // get the height of module children
      const childrenHeight = moduleChildren.reduce((acc, el) => acc + el.clientHeight, 0);

      // we need to consider CardContainer's padding when calculating height since we're using border-box
      const { paddingTop, paddingBottom } = calculateCardPadding(module.position);
      const gridHeight = childrenHeight + paddingTop + paddingBottom;

      // resize observer's callback fires each time this useEffect runs, so we only want to set state if the heights are different
      if (gridHeight === module.position.height) return;

      onModuleHeightUpdate(module.token, gridHeight);
    });

    moduleChildren.forEach((el) => resizeObserver.observe(el));

    return () => {
      moduleChildren.forEach((el) => resizeObserver.unobserve(el));
    };
  }, [module, hasDynamicHeight, onModuleHeightUpdate]);

  return (
    <ModuleContainer position={module.position}>
      <ModulePrintWrapper>
        <StyledModuleCard ref={ref} disableOverflow={hasDynamicHeight}>
          {children}
        </StyledModuleCard>
      </ModulePrintWrapper>
    </ModuleContainer>
  );
}
